import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { ToastContainer } from "react-toastify";
import ConfirmDelete from "@/components/ConfirmDelete";
import ReferrersDialog from "@/components/ReferrersDialog";
import { isDeleteRefusal, refusalMessage } from "@/services/deleteRefusal";
import {
  getClientsByCountryCode,
  deleteClient,
} from "@/services/clientService/clientActions";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import NotificationAlertOptions from "@/layouts/Alerts";
import DisplayClientsView from "./DisplayClientsView";
import type { ClientsResponse } from "@/../../shared/apiTypes";

const DisplayClientsContainer = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const clientsRedux = (useAppSelector((s) => s.clients.clientsCountryCode) as any);
  const permission = useCurrentPermission();
  const countryCode = useAppSelector((s) => s.sites.site.country_code);

  // Local state
  const [clients, setClients] = useState<ClientsResponse>([]);
  const [alert, setAlert] = useState<React.ReactNode>(null);

  // Charger les clients au montage et quand le countryCode change
  useEffect(() => {
    if (countryCode !== undefined) {
      dispatch(getClientsByCountryCode(countryCode));
    }
  }, [countryCode, dispatch]);

  // Mettre à jour la liste quand Redux change
  useEffect(() => {
    if (Array.isArray(clientsRedux)) {
      setClients(clientsRedux);
    }
  }, [clientsRedux]);

  // Fonctions d'alerte
  const hideAlert = () => setAlert(null);

  // SCRUM-2356: a delete refused because products or ads still belong to the
  // client opens the SCRUM-2352 pop-up; appErrorMiddleware leaves that payload
  // alone, so the message shows once. Other failures stay with the hub.
  const showReferrers = (err: unknown) => {
    if (!isDeleteRefusal(err)) return;
    setAlert(
      <ReferrersDialog
        message={refusalMessage(err)}
        referrers={err.referrers}
        onClose={hideAlert}
      />
    );
  };

  const questionAlert = (id) => {
    setAlert(
      // SCRUM-1018 lives in ConfirmDelete now: it awaits `onConfirm` and only
      // then calls `onSuccess`, so a failed delete cannot report success. The
      // failure is surfaced by the central hub + the errors effect above.
      <ConfirmDelete
        title="Would you like to remove this Client?"
        onClose={hideAlert}
        onConfirm={() => dispatch(deleteClient(id, (countryCode as string))).unwrap()}
        onSuccess={() =>
          NotificationAlertOptions("success", "Client Deleted with success")
        }
        onError={showReferrers}
      >
        Delete Operation
      </ConfirmDelete>
    );
  };

  // Colonnes de la table
  const columns = useMemo(
    () => [
      {
        accessorKey: "logo",
        header: "Logo",
        cell: ({ row }) => (
          <img
            className="avatar rounded-circle mr-3"
            src={row.original.clientImg || ""}
            alt="Client Logo"
          />
        ),
      },
      { accessorKey: "name", header: "Name" },
      { accessorKey: "legal_name", header: "Legal Name" },
      { accessorKey: "adresse", header: "Adresse" },
      {
        accessorFn: (row) => row.contacts?.website,
        header: "Website",
      },
      {
        header: "ACTION",
        cell: ({ row }) => (
          <div>
            <Link
              to={"/admin/client/update/" + row.original._id}
              className="btn btn-sm btn-primary mr-2"
            >
              <i className="ni ni-settings-gear-65 mr-1" />
              Details
            </Link>
            {(permission?.edit || permission?.publish) && (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => questionAlert(row.original._id)}
              >
                <i className="fas fa-trash mr-1" />
                Delete
              </button>
            )}
          </div>
        ),
      },
    ],
    [permission]
  );

  return (
    <DisplayClientsView
      clients={clients}
      columns={columns}
      alert={alert}
      ToastContainer={ToastContainer}
    />
  );
};

export default DisplayClientsContainer;
